import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import { cn } from "../../lib/utils";

/**
 * ProjectShowcase — Animated campaign/testimonial carousel.
 * Based on Aceternity's Animated Testimonials pattern.
 *
 * Props:
 *   items: [{ name, designation, quote, src, link }]
 *   className
 */
export function ProjectShowcase({
  items = [],
  className,
  autoPlay = true,
  autoPlayInterval = 5000,
}) {
  const [active, setActive] = useState(0);
  const [direction, setDirection] = useState(1);

  const handleNext = () => {
    setDirection(1);
    setActive((prev) => (prev + 1) % items.length);
  };

  const handlePrev = () => {
    setDirection(-1);
    setActive((prev) => (prev - 1 + items.length) % items.length);
  };

  useEffect(() => {
    if (!autoPlay || items.length <= 1) return;
    const interval = setInterval(handleNext, autoPlayInterval);
    return () => clearInterval(interval);
  }, [autoPlay, autoPlayInterval, items.length]);

  if (!items.length) return null;

  const current = items[active];

  const slideVariants = {
    enter: (dir) => ({
      y: dir > 0 ? 40 : -40,
      opacity: 0,
    }),
    center: {
      y: 0,
      opacity: 1,
    },
    exit: (dir) => ({
      y: dir > 0 ? -40 : 40,
      opacity: 0,
    }),
  };

  return (
    <div className={cn("relative max-w-4xl mx-auto", className)}>
      <div className="relative grid grid-cols-1 md:grid-cols-2 gap-8 items-center min-h-[400px]">
        {/* Image side */}
        <div className="relative h-[300px] md:h-[400px] w-full rounded-2xl overflow-hidden shadow-xl">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={active}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-0"
            >
              {current.src ? (
                <img
                  src={current.src}
                  alt={current.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-primary/20 to-blue-400/20 flex items-center justify-center">
                  <span className="text-6xl font-bold text-primary/30">
                    {current.name?.charAt(0) || "?"}
                  </span>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Content side */}
        <div className="relative flex flex-col justify-center px-2 md:px-6">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={active}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1], delay: 0.05 }}
            >
              {current.designation && (
                <span className="inline-block text-sm font-semibold text-primary uppercase tracking-wide mb-3">
                  {current.designation}
                </span>
              )}
              <h3 className="text-xl font-medium text-gray-900 mb-3">
                {current.name}
              </h3>
              <p className="text-gray-500 leading-relaxed">
                {current.quote}
              </p>
              {current.link && (
                <a
                  href={current.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 mt-6 text-sm font-medium text-primary hover:text-primary-dark transition-colors"
                >
                  Learn more
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Navigation buttons */}
          <div className="flex items-center gap-3 mt-8">
            <button
              onClick={handlePrev}
              disabled={items.length <= 1}
              className="flex items-center justify-center w-10 h-10 rounded-full border border-gray-200 bg-white shadow-sm hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              aria-label="Previous"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
            <button
              onClick={handleNext}
              disabled={items.length <= 1}
              className="flex items-center justify-center w-10 h-10 rounded-full border border-gray-200 bg-white shadow-sm hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              aria-label="Next"
            >
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </button>
            {/* Dots indicator */}
            <div className="flex items-center gap-1.5 ml-2">
              {items.map((_, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setDirection(i > active ? 1 : -1);
                    setActive(i);
                  }}
                  className={cn(
                    "h-1.5 rounded-full transition-all duration-300",
                    i === active ? "w-6 bg-primary" : "w-1.5 bg-gray-300 hover:bg-gray-400"
                  )}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProjectShowcase;
